import React, { useState, useCallback } from 'react';
import api from '../services/api';
import { useAutoRefresh, AutoRefreshBadge } from '../hooks/useAutoRefresh';

export default function Analytics() {
  const [stats, setStats] = useState(null);
  const [error, setError] = useState(null);

  const fetchAnalytics = useCallback(async () => {
    try {
      const response = await api.get('/analytics');
      setStats(response.data);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch analytics:', err);
      setError('Failed to load analytics');
    }
  }, []);

  const { loading, refreshing, lastUpdated, refresh } = useAutoRefresh(fetchAnalytics, [fetchAnalytics]);

  if (loading && !stats) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-xl">Loading analytics...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-xl text-red-600">{error}</div>
      </div>
    );
  }

  return (
    <div className={`${refreshing ? 'opacity-90' : ''} transition-opacity`}>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Analytics</h1>
          <AutoRefreshBadge refreshing={refreshing} lastUpdated={lastUpdated} className="mt-1" />
        </div>
        <button
          type="button"
          onClick={refresh}
          disabled={refreshing}
          className="px-3 py-2 text-sm font-medium rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
        >
          Refresh
        </button>
      </div>

      {/* Overview Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">Total Users</div>
          <div className="text-3xl font-bold text-gray-900 dark:text-white">
            {stats?.users?.total || 0}
          </div>
          <div className="text-xs text-green-600 mt-1">
            {stats?.users?.active || 0} active
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">Total Groups</div>
          <div className="text-3xl font-bold text-gray-900 dark:text-white">
            {stats?.groups?.total || 0}
          </div>
          <div className="text-xs text-green-600 mt-1">
            {stats?.groups?.active || 0} active
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">Total Revenue</div>
          <div className="text-3xl font-bold text-green-600">
            ${stats?.revenue?.total?.toFixed(2) || '0.00'}
          </div>
          <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">All time</div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">Active Credits</div>
          <div className="text-3xl font-bold text-blue-600">
            {stats?.credits?.active?.toFixed(2) || '0.00'}
          </div>
          <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">Cook Credits</div>
        </div>
      </div>

      {/* Membership Tiers */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 mb-6">
        <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">Membership Distribution</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="border rounded-lg p-4">
            <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">Starter Tier</div>
            <div className="text-2xl font-bold text-gray-700 dark:text-gray-200">
              {stats?.tiers?.free || 0}
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              {stats?.tiers?.total ? ((stats.tiers.free / stats.tiers.total) * 100).toFixed(1) : 0}%
            </div>
          </div>
          <div className="border rounded-lg p-4">
            <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">Premium Tier</div>
            <div className="text-2xl font-bold text-blue-600">
              {stats?.tiers?.premium || 0}
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              {stats?.tiers?.total ? ((stats.tiers.premium / stats.tiers.total) * 100).toFixed(1) : 0}%
            </div>
          </div>
          <div className="border rounded-lg p-4">
            <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">Alpha Tier</div>
            <div className="text-2xl font-bold text-amber-600">
              {stats?.tiers?.alpha || 0}
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              {stats?.tiers?.total ? ((stats.tiers.alpha / stats.tiers.total) * 100).toFixed(1) : 0}%
            </div>
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">Deposit Activity</h2>
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600 dark:text-gray-400">Total Deposits</span>
              <span className="font-medium">{stats?.deposits?.total || 0}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600 dark:text-gray-400">Pending</span>
              <span className="font-medium text-yellow-600">{stats?.deposits?.pending || 0}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600 dark:text-gray-400">Confirmed</span>
              <span className="font-medium text-green-600">{stats?.deposits?.confirmed || 0}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600 dark:text-gray-400">Swept</span>
              <span className="font-medium text-blue-600">{stats?.deposits?.swept || 0}</span>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">Usage Statistics</h2>
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600 dark:text-gray-400">Total Transactions</span>
              <span className="font-medium">{stats?.transactions?.total || 0}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600 dark:text-gray-400">Tokens Tracked</span>
              <span className="font-medium">{stats?.usage?.tokensTracked || 0}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600 dark:text-gray-400">Alerts Sent</span>
              <span className="font-medium">{stats?.usage?.alertsSent || 0}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600 dark:text-gray-400">Total Spend</span>
              <span className="font-medium">{stats?.credits?.spent?.toFixed(2) || '0.00'} credits</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
